import React, { useState, useEffect, useRef, useCallback } from 'react';
import SendMessageDrawer from './SendMessageDrawer';
import { api } from '../../lib/api';
import {
  X,
  Mail,
  Phone,
  AlertTriangle,
  ChevronDown,
  Check,
  Pencil,
  MessageSquare,
  Clock,
  FileText,
  Info,
  CalendarDays,
  Pill,
  Copy,
  Shield,
  MessageCircle,
} from 'lucide-react';
import { Listbox } from '@headlessui/react';
import { UI_STATUSES, CLOSED_REASONS, getStatusObject, normalizeStatus } from '../../utils/statuses';
import { RISK_LEVELS } from '../ui/riskLevels';
import { usePatients } from '../../hooks/usePatients';
import {
  formatPhone,
  formatProvider,
  formatInsurance,
  formatAiReason,
  formatCondition,
  formatMedication,
  formatRiskFactor,
  isSpecialtyDrug,
} from '../../utils/formatters';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// --- Utility functions ---

function computeAge(dob) {
  if (!dob) return null;
  const d = dayjs(dob);
  if (!d.isValid()) return null;
  return dayjs().diff(d, 'year');
}

function formatDob(dob) {
  if (!dob) return null;
  const d = dayjs(dob);
  if (!d.isValid()) return null;
  return d.format('MM/DD/YYYY');
}

function getOutreachIcon(text) {
  const lower = (text || '').toLowerCase();
  if (/screening|overdue|due|booster/.test(lower)) {
    return <CalendarDays className="h-3.5 w-3.5 text-red-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />;
  }
  if (/refill|medication|drug|adherence/.test(lower)) {
    return <Pill className="h-3.5 w-3.5 text-red-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />;
  }
  return <AlertTriangle className="h-3.5 w-3.5 text-red-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />;
}

const RULE_SUGGESTIONS = {
  overdue_appointment: 'Call to schedule a follow-up visit',
  flag_urgent_status: 'Prioritize outreach — EHR flagged as urgent',
  med_adherence_risk: 'Check on medication refill and adherence',
  callouts_high_risk: 'Review EHR flags and reach out promptly',
  escalate_no_response: 'Try alternate contact method or escalate',
  missed_visits_flag: 'Reach out about missed appointments',
  callouts_multiple_flags: 'Review multiple EHR flags and reach out promptly',
  callouts_medium_risk: 'Review EHR flags and follow up',
  flag_concerning_status: 'Review concerning EHR status and follow up',
};

function getSuggestedApproach(patient) {
  const suggestions = [];

  // Map from triggered_rules
  if (patient.triggered_rules?.length > 0) {
    for (const rule of patient.triggered_rules) {
      if (RULE_SUGGESTIONS[rule] && !suggestions.includes(RULE_SUGGESTIONS[rule])) {
        suggestions.push(RULE_SUGGESTIONS[rule]);
      }
    }
  }

  // Fallback to AI risk_recommendations
  if (suggestions.length === 0 && patient.risk_recommendations?.length > 0) {
    suggestions.push(patient.risk_recommendations[0]);
  }

  // Generic fallback by risk level
  if (suggestions.length === 0) {
    const risk = (patient.risk || '').toLowerCase();
    if (risk === 'high') {
      suggestions.push('Prioritize outreach — this patient needs attention soon');
    } else if (risk === 'medium') {
      suggestions.push('Review patient details and schedule a follow-up');
    } else {
      suggestions.push('Check in with patient during next routine outreach');
    }
  }

  return suggestions;
}

function copyToClipboard(text) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text);
  }
}

function parseCommaList(str) {
  if (!str || typeof str !== 'string') return [];
  return str.split(',').map(s => s.trim()).filter(Boolean);
}

function getRiskLevelObj(risk) {
  return RISK_LEVELS.find(r => r.value.toLowerCase() === (risk || '').toLowerCase()) || RISK_LEVELS[0];
}

// --- Component ---

const PatientDrawer = ({ isOpen, onClose, patient, forceCloseOnBulk, onPatientUpdate }) => {
  const { updatePatient } = usePatients();
  const drawerRef = useRef(null);
  const [isSendDrawerOpen, setIsSendDrawerOpen] = useState(false);
  const [riskLevel, setRiskLevel] = useState(getRiskLevelObj(patient?.risk));
  const [riskSaving, setRiskSaving] = useState(false);

  const [statusObj, setStatusObj] = useState(getStatusObject(patient?.status));
  const [statusSaving, setStatusSaving] = useState(false);

  // Recovered/Closed flow state
  const [recoveryNote, setRecoveryNote] = useState('');
  const [closedReason, setClosedReason] = useState('');
  const [closedNote, setClosedNote] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null);

  const [editingEmail, setEditingEmail] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [savingContact, setSavingContact] = useState(false);

  // Copy feedback
  const [copiedField, setCopiedField] = useState(null);

  // Activity timeline
  const [outreachLogs, setOutreachLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const [visible, setVisible] = useState(false);

  // Animation: delay visibility for slide-in
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!patient) return;
    setNewEmail(patient.email || '');
    setNewPhone(patient.phone || '');
    setRiskLevel(getRiskLevelObj(patient.risk));
    setStatusObj(getStatusObject(patient.status));
    setEditingEmail(false);
    setEditingPhone(false);
    setPendingStatus(null);
    setRecoveryNote('');
    setClosedReason('');
    setClosedNote('');
  }, [patient]);

  // Fetch outreach logs
  const fetchOutreachLogs = useCallback(async () => {
    if (!patient?.id) return;
    setLogsLoading(true);
    try {
      const response = await api.getPatientOutreachLogs(patient.id);
      setOutreachLogs((response.logs || []).slice(0, 10));
    } catch (error) {
      console.error('Failed to fetch outreach logs:', error);
      setOutreachLogs([]);
    }
    setLogsLoading(false);
  }, [patient?.id]);

  useEffect(() => {
    if (isOpen && patient?.id) {
      fetchOutreachLogs();
    }
  }, [isOpen, patient?.id, fetchOutreachLogs]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !patient || (typeof forceCloseOnBulk === 'function' && forceCloseOnBulk())) return null;

  const hasContact = newEmail || newPhone;

  // Save risk level
  const handleRiskLevelChange = async (level) => {
    setRiskLevel(level);
    setRiskSaving(true);
    try {
      await api.updatePatient(patient.id, { risk: level.value });
      onPatientUpdate?.(patient.id, { risk: level.value });
    } catch (error) {
      console.error('Failed to update risk level:', error);
    }
    setRiskSaving(false);
  };

  // Handle status selection from dropdown
  const handleStatusSelect = (obj) => {
    if (obj.value === 'recovered') {
      setPendingStatus('recovered');
      setRecoveryNote('');
      return;
    }
    if (obj.value === 'closed') {
      setPendingStatus('closed');
      setClosedReason('');
      setClosedNote('');
      return;
    }
    setPendingStatus(null);
    saveStatus(obj);
  };

  // Save status (shared by immediate and confirmed flows)
  const saveStatus = async (obj, extraFields = {}) => {
    const previousStatus = statusObj;
    setStatusObj(obj);
    setStatusSaving(true);
    const updates = { status: obj.value, ...extraFields };
    if (normalizeStatus(obj.value) === 'contacted' && normalizeStatus(patient.status) !== 'contacted') {
      updates.last_touch = new Date().toISOString();
    }
    try {
      await api.updatePatient(patient.id, updates);
      const updatedFields = { status: obj.value, ...extraFields };
      if (updates.last_touch) updatedFields.last_touch = updates.last_touch;
      await updatePatient(patient.id, updatedFields);
      onPatientUpdate?.(patient.id, updatedFields);
      if (obj.value === 'contacted') {
        try {
          await api.createOutreachLog({
            patient_id: patient.id,
            channel: 'manual',
            notes: 'Manually marked as contacted',
          });
        } catch (logError) {
          console.error('Failed to create outreach log:', logError);
        }
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      setStatusObj(previousStatus);
    }
    setStatusSaving(false);
    setPendingStatus(null);
  };

  // Confirm recovered
  const handleConfirmRecovered = () => {
    if (recoveryNote.trim().length < 10) return;
    const obj = UI_STATUSES.find(s => s.value === 'recovered');
    saveStatus(obj, { recovery_note: recoveryNote.trim() });
  };

  // Confirm closed
  const handleConfirmClosed = () => {
    if (!closedReason) return;
    const obj = UI_STATUSES.find(s => s.value === 'closed');
    saveStatus(obj, {
      closed_reason: closedReason,
      ...(closedNote.trim() ? { closed_note: closedNote.trim() } : {}),
    });
  };

  // Save email
  const handleSaveEmail = async () => {
    const emailRegex = /^\S+@\S+\.\S+$/;
    if (newEmail && !emailRegex.test(newEmail)) {
      setEmailError('Invalid email');
      return;
    }
    setSavingContact(true);
    try {
      await api.updatePatient(patient.id, { email: newEmail });
      setEditingEmail(false);
      setEmailError('');
      onPatientUpdate?.(patient.id, { email: newEmail });
    } catch (error) {
      console.error('Failed to update email:', error);
      setEmailError('Failed to save');
    }
    setSavingContact(false);
  };

  // Save phone
  const handleSavePhone = async () => {
    const phoneRegex = /^\d{10,15}$/;
    if (newPhone && !phoneRegex.test(newPhone.replace(/\D/g, ''))) {
      setPhoneError('Invalid phone');
      return;
    }
    setSavingContact(true);
    try {
      await api.updatePatient(patient.id, { phone: newPhone });
      setEditingPhone(false);
      setPhoneError('');
      onPatientUpdate?.(patient.id, { phone: newPhone });
    } catch (error) {
      console.error('Failed to update phone:', error);
      setPhoneError('Failed to save');
    }
    setSavingContact(false);
  };

  const getRiskColor = (value) => {
    switch (value?.toLowerCase()) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-amber-600 bg-amber-50';
      case 'low': return 'text-emerald-600 bg-emerald-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'sms': return <MessageSquare className="h-3.5 w-3.5" strokeWidth={1.5} />;
      case 'email': return <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />;
      case 'secure_message': return <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />;
      default: return <Info className="h-3.5 w-3.5" strokeWidth={1.5} />;
    }
  };

  const handleCopy = (text, field) => {
    copyToClipboard(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  // Build header subline parts
  const age = computeAge(patient.dob);
  const dob = formatDob(patient.dob);
  const sublineParts = [];
  if (age !== null) sublineParts.push(`${age}y`);
  if (dob) sublineParts.push(`DOB ${dob}`);
  if (patient.provider) sublineParts.push(formatProvider(patient.provider));

  // Build "Why They Need Outreach" lines
  // Prefer translated_flags (human-readable sentences) over raw ai_reason
  const outreachLines = [];
  if (patient.translated_flags?.length > 0) {
    patient.translated_flags.forEach(f => outreachLines.push(f.display_text));
  } else {
    // Fallback for patients imported before flag translation
    if (patient.ai_reason) {
      outreachLines.push(formatAiReason(patient.ai_reason));
    }
    if (patient.last_touch || patient.lastTouch) {
      const lastDate = patient.last_touch || patient.lastTouch;
      const days = dayjs().diff(dayjs(lastDate), 'day');
      const visitContext = days > 90
        ? `Last visit was ${days} days ago — no follow-up scheduled`
        : days > 30
          ? `Last visit was ${days} days ago`
          : null;
      if (visitContext && !outreachLines.some(l => l.includes('Last visit'))) {
        outreachLines.push(visitContext);
      }
    }
    if (patient.risk_factors?.length > 0) {
      patient.risk_factors.forEach(f => {
        const formatted = formatRiskFactor(f) || f;
        if (!outreachLines.some(l => l.includes(f))) outreachLines.push(formatted);
      });
    }
  }

  // Build suggested approach — prefer stored patient-specific approach
  const suggestions = patient.suggested_approach
    ? [patient.suggested_approach]
    : getSuggestedApproach(patient);

  // Contact data
  const conditions = parseCommaList(patient.conditions || patient.diagnoses);
  const medications = parseCommaList(patient.medications);

  // Format last visit for header
  const formatLastVisit = (date) => {
    if (!date) return null;
    const d = dayjs(date);
    if (!d.isValid()) return null;
    return d.format('MMM D, YYYY');
  };
  const lastVisitDisplay = formatLastVisit(patient.last_touch || patient.lastTouch);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/20 z-[60] transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-labelledby="patient-drawer-title"
        className={`fixed top-0 right-0 w-full sm:w-[420px] h-full bg-white shadow-xl z-[70] flex flex-col transition-transform duration-200 ease-out ${visible ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="flex items-start justify-between">
            <h2 id="patient-drawer-title" className="text-lg font-semibold text-slate-900 truncate">
              {patient.name}
            </h2>
            <button
              onClick={onClose}
              className="p-2 -mr-2 -mt-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>

          {sublineParts.length > 0 && (
            <p className="text-sm text-slate-500 mt-0.5">
              {sublineParts.join(' · ')}
            </p>
          )}

          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              {/* Risk Badge — clickable Listbox */}
              <Listbox value={riskLevel} onChange={handleRiskLevelChange} disabled={riskSaving}>
                <div className="relative">
                  <Listbox.Button className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer hover:ring-2 hover:ring-offset-1 transition-all ${getRiskColor(riskLevel.value)} ${riskSaving ? 'opacity-60' : ''}`}>
                    {riskLevel.value}
                    <ChevronDown className="h-3 w-3" strokeWidth={2} />
                  </Listbox.Button>
                  <Listbox.Options className="absolute z-10 mt-1 w-36 bg-white border border-slate-200 rounded-lg shadow-lg py-1">
                    {RISK_LEVELS.map((level) => (
                      <Listbox.Option
                        key={level.value}
                        value={level}
                        className={({ active }) =>
                          `cursor-pointer px-3 py-2 text-sm ${active ? 'bg-slate-50' : ''}`
                        }
                      >
                        {({ selected }) => (
                          <div className="flex items-center justify-between">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRiskColor(level.value)}`}>
                              {level.value}
                            </span>
                            {selected && <Check className="h-3.5 w-3.5 text-slate-600" strokeWidth={1.5} />}
                          </div>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </Listbox>

              {/* Status Badge — clickable Listbox */}
              <Listbox value={statusObj} onChange={handleStatusSelect} disabled={statusSaving}>
                <div className="relative">
                  <Listbox.Button className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer hover:ring-2 hover:ring-offset-1 transition-all ${statusObj.color} ${statusSaving ? 'opacity-60' : ''}`}>
                    {statusObj.label}
                    <ChevronDown className="h-3 w-3" strokeWidth={2} />
                  </Listbox.Button>
                  <Listbox.Options className="absolute z-10 mt-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto">
                    {UI_STATUSES.map((s) => (
                      <Listbox.Option
                        key={s.value}
                        value={s}
                        className={({ active }) =>
                          `cursor-pointer px-3 py-2 text-sm ${active ? 'bg-slate-50' : ''}`
                        }
                      >
                        {({ selected }) => (
                          <div className="flex items-center justify-between">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.color}`}>
                              {s.label}
                            </span>
                            {selected && <Check className="h-3.5 w-3.5 text-slate-600" strokeWidth={1.5} />}
                          </div>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </Listbox>
            </div>

            {lastVisitDisplay && (
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" strokeWidth={1.5} />
                {lastVisitDisplay}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* WHY THEY NEED OUTREACH */}
          {outreachLines.length > 0 && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-xs font-semibold text-red-800 uppercase tracking-wider">Why They Need Outreach</span>
              </div>
              <ul className="space-y-1.5 ml-1">
                {outreachLines.slice(0, 4).map((line, i) => (
                  <li key={i} className="text-sm text-red-700 flex items-start gap-2">
                    {getOutreachIcon(line)}
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* SUGGESTED APPROACH */}
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
            <div className="flex items-center gap-2 mb-2">
              <MessageCircle className="h-4 w-4 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
              <span className="text-xs font-semibold text-blue-800 uppercase tracking-wider">Suggested Approach</span>
            </div>
            <ul className="space-y-1.5 ml-1">
              {suggestions.map((s, i) => (
                <li key={i} className="text-sm text-blue-700 flex items-start gap-2">
                  <span className="text-blue-400 mt-0.5 flex-shrink-0">&rarr;</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Recovered confirmation */}
          {pendingStatus === 'recovered' && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-3">
              <p className="text-sm font-medium text-green-800">Mark as Recovered</p>
              <textarea
                value={recoveryNote}
                onChange={(e) => setRecoveryNote(e.target.value)}
                placeholder="How was this patient recovered? (min 10 characters)"
                className="w-full px-3 py-2 text-sm border border-green-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-400 bg-white resize-none"
                rows={3}
                autoFocus
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-green-600">
                  {recoveryNote.trim().length}/10 characters minimum
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPendingStatus(null)}
                    className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmRecovered}
                    disabled={recoveryNote.trim().length < 10 || statusSaving}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      recoveryNote.trim().length >= 10
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-green-200 text-green-400 cursor-not-allowed'
                    }`}
                  >
                    {statusSaving ? 'Saving...' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Closed confirmation */}
          {pendingStatus === 'closed' && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
              <p className="text-sm font-medium text-slate-800">Close Case</p>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Reason (required)</label>
                <select
                  value={closedReason}
                  onChange={(e) => setClosedReason(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                >
                  <option value="">Select a reason...</option>
                  {CLOSED_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <textarea
                value={closedNote}
                onChange={(e) => setClosedNote(e.target.value)}
                placeholder="Additional notes (optional)"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white resize-none"
                rows={2}
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setPendingStatus(null)}
                  className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmClosed}
                  disabled={!closedReason || statusSaving}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    closedReason
                      ? 'bg-slate-800 text-white hover:bg-slate-900'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  {statusSaving ? 'Saving...' : 'Close Case'}
                </button>
              </div>
            </div>
          )}

          {/* CONTACT */}
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3 block">
              Contact
            </label>
            <div className="space-y-2">
              {/* Phone */}
              <div className="group flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" strokeWidth={1.5} />
                {editingPhone ? (
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      type="tel"
                      value={newPhone}
                      onChange={e => {
                        setNewPhone(e.target.value);
                        setPhoneError('');
                      }}
                      onKeyDown={e => e.key === 'Enter' && handleSavePhone()}
                      className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-400"
                      placeholder="(555) 123-4567"
                      autoFocus
                    />
                    <button
                      onClick={handleSavePhone}
                      disabled={savingContact}
                      className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                    >
                      <Check className="h-4 w-4" strokeWidth={2} />
                    </button>
                    <button
                      onClick={() => { setEditingPhone(false); setNewPhone(patient.phone || ''); setPhoneError(''); }}
                      className="p-1 text-slate-400 hover:bg-slate-100 rounded transition-colors"
                    >
                      <X className="h-4 w-4" strokeWidth={2} />
                    </button>
                  </div>
                ) : (
                  <>
                    <span className={`flex-1 text-sm ${newPhone ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                      {newPhone ? formatPhone(newPhone) : 'No phone'}
                    </span>
                    {newPhone && (
                      <button
                        onClick={() => handleCopy(newPhone, 'phone')}
                        className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors opacity-0 group-hover:opacity-100"
                        title="Copy phone"
                      >
                        {copiedField === 'phone' ? <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={2} /> : <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />}
                      </button>
                    )}
                    <button
                      onClick={() => setEditingPhone(true)}
                      className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </>
                )}
              </div>
              {phoneError && <p className="text-xs text-red-500 ml-1">{phoneError}</p>}

              {/* Email */}
              {(newEmail || editingEmail) && (
                <div className="group flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                  <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" strokeWidth={1.5} />
                  {editingEmail ? (
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="email"
                        value={newEmail}
                        onChange={e => {
                          setNewEmail(e.target.value);
                          setEmailError('');
                        }}
                        onKeyDown={e => e.key === 'Enter' && handleSaveEmail()}
                        className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-400"
                        placeholder="email@example.com"
                        autoFocus
                      />
                      <button
                        onClick={handleSaveEmail}
                        disabled={savingContact}
                        className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                      >
                        <Check className="h-4 w-4" strokeWidth={2} />
                      </button>
                      <button
                        onClick={() => { setEditingEmail(false); setNewEmail(patient.email || ''); setEmailError(''); }}
                        className="p-1 text-slate-400 hover:bg-slate-100 rounded transition-colors"
                      >
                        <X className="h-4 w-4" strokeWidth={2} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-slate-700 truncate">{newEmail}</span>
                      <button
                        onClick={() => handleCopy(newEmail, 'email')}
                        className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors opacity-0 group-hover:opacity-100"
                        title="Copy email"
                      >
                        {copiedField === 'email' ? <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={2} /> : <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />}
                      </button>
                      <button
                        onClick={() => setEditingEmail(true)}
                        className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </>
                  )}
                </div>
              )}
              {emailError && <p className="text-xs text-red-500 ml-1">{emailError}</p>}

              {/* Insurance */}
              {patient.insurance && (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                  <Shield className="h-4 w-4 text-slate-400 flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-sm text-slate-700">{formatInsurance(patient.insurance) || patient.insurance}</span>
                </div>
              )}

              {/* Conditions */}
              {conditions.length > 0 && (
                <div className="pt-1">
                  <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Conditions</span>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {conditions.map((c, i) => (
                      <span key={i} className="px-2.5 py-1 rounded-full bg-slate-100 text-xs text-slate-600">
                        {formatCondition(c) || c}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Medications */}
              {medications.length > 0 && (
                <div className="pt-1">
                  <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Medications</span>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {medications.map((m, i) => {
                      const specialty = isSpecialtyDrug(m);
                      return (
                        <span
                          key={i}
                          className={`px-2.5 py-1 rounded-full text-xs ${
                            specialty
                              ? 'bg-red-50 text-red-700 ring-1 ring-red-200'
                              : 'bg-amber-50 text-amber-700'
                          }`}
                        >
                          {formatMedication(m) || m}
                          {specialty && (
                            <span className="ml-1 text-[10px] font-medium text-red-500">Specialty</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ACTIVITY */}
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3 block">
              Activity
            </label>
            {logsLoading ? (
              <div className="flex items-center justify-center py-6">
                <div className="w-4 h-4 border-2 border-slate-200 border-t-slate-500 rounded-full animate-spin" />
              </div>
            ) : outreachLogs.length === 0 ? (
              <p className="text-sm text-slate-400 italic py-3">No outreach activity yet — this will be the first contact.</p>
            ) : (
              <div className="relative pl-5 space-y-0">
                {/* Vertical line */}
                <div className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-200" />
                {outreachLogs.map((log, i) => (
                  <div key={log.id || i} className="relative pb-4 last:pb-0">
                    {/* Dot */}
                    <div className="absolute -left-5 top-0.5 w-3.5 h-3.5 rounded-full bg-white border-2 border-slate-300 flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-slate-400 mt-0.5">{getChannelIcon(log.channel)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-slate-700 capitalize">
                            {log.type || log.channel || 'Outreach'}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {log.created_at ? dayjs(log.created_at).fromNow() : ''}
                          </span>
                        </div>
                        {log.notes && (
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{log.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pt-4 pb-8 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={() => setIsSendDrawerOpen(true)}
            disabled={!hasContact}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              hasContact
                ? 'bg-slate-900 text-white hover:bg-slate-800'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            <MessageSquare className="h-4 w-4" strokeWidth={1.5} />
            {hasContact ? 'Send Message' : 'Add contact info to message'}
          </button>
        </div>
      </div>

      <SendMessageDrawer
        isOpen={isSendDrawerOpen}
        onClose={() => setIsSendDrawerOpen(false)}
        patient={{
          ...patient,
          email: newEmail,
          phone: newPhone,
        }}
      />
    </>
  );
};

export default PatientDrawer;
